import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { AgGridTable, Pagination } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { FaShieldAlt, FaSearch, FaTimes, FaSyncAlt } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { MdFilterAlt } from 'react-icons/md';
import { getWarranties, exportData as exportWarranty, setNestedParams } from 'redux/crm/project/warrantySlice';
import AdvanceFilterDrawer, { getEmptyValues } from 'components/common/AdvanceFilterDrawer';
import ProjectLinkRenderer from 'components/app/crm/warranty/component/ProjectLinkRenderer';
import { PersonRenderer } from 'components/common/ag-grid';
import { LuMapPin } from 'react-icons/lu';
import { debounce } from 'helpers/debounce';

// Location with a map-pin mark, matching the icon treatment on the project list.
const LocationRenderer = ({ value }) =>
  value ? (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      <LuMapPin size={13} className="tw-text-slate-400 tw-shrink-0" />
      {value}
    </span>
  ) : null;
LocationRenderer.propTypes = { value: PropTypes.string };

const SEARCH_FIELD = 'project_name_or_project_j_number_cont';

const FILTER_FIELDS = [
  { key: 'contract_addendum_number', label: 'Contract / Addendum', type: 'text', paramKey: 'contract_addendum_number_cont', tagName: 'Contract' },
  { key: 'cse_incharge', label: 'CSE In-charge', type: 'text', paramKey: 'cse_incharge_cont', tagName: 'CSE' },
  { key: 'created_at', label: 'Created At', type: 'date_range', paramKeys: ['created_at_gteq', 'created_at_lteq'], tagName: 'Created' },
];

const COLUMN_DEFS = [
  { field: 'project.j_number', headerName: 'Job number', width: 120, pinned: 'left', sortable: false, cellRenderer: ProjectLinkRenderer },
  { field: 'project.name', headerName: 'Project Name', flex: 1, minWidth: 240, sortable: false, cellRenderer: ProjectLinkRenderer },
  { field: 'contract_addendum_number', headerName: 'Contract / Addendum No.', width: 180, sortable: true },
  { field: 'cse_incharge', headerName: 'CSE In-charge', width: 180, sortable: true, cellRenderer: PersonRenderer },
  { field: 'design_warranty_start', headerName: 'Design Warranty Start', width: 180, sortable: true },
  { field: 'design_warranty_finish', headerName: 'Design Warranty Finish', width: 180, sortable: true },
  { field: 'leak_warranty_start', headerName: 'Leak Warranty Start', width: 180, sortable: true },
  { field: 'leak_warranty_finish', headerName: 'Leak Warranty Finish', width: 180, sortable: true },
  { field: 'location', headerName: 'Location', width: 180, sortable: true, cellRenderer: LocationRenderer },
  { field: 'created_date', headerName: 'Created At', width: 140, sortable: false },
];

const WarrantyPage = () => {
  const dispatch = useDispatch();

  const rowData   = useSelector((s) => s.crm_warranty.allData.data);
  const totalRows = useSelector((s) => s.crm_warranty.allData.totalRows);
  const loading   = useSelector((s) => s.crm_warranty.loading);
  const exporting = useSelector((s) => s.crm_warranty.exporting);

  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort]       = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]   = useState('');
  const [filterValues, setFilterValues]           = useState(() => getEmptyValues(FILTER_FIELDS));
  const [filterTags, setFilterTags]               = useState([]);
  const [showFilter, setShowFilter]               = useState(false);

  const activeFilterParamsRef = useRef({});
  const searchRef = useRef('');

  const totalPages = Math.ceil((totalRows || 0) / perPage) || 1;

  const buildParams = useCallback(
    (overrides = {}) => ({
      page, per_page: perPage, sort,
      ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}),
      ...activeFilterParamsRef.current,
      ...overrides,
    }),
    [page, perPage, sort]
  );

  const fetchData = useCallback((params) => {
    dispatch(setNestedParams({ key: 'allData', params }));
    dispatch(getWarranties(params));
  }, [dispatch]);

  useEffect(() => { fetchData(buildParams()); }, [fetchData, buildParams]);

  const debouncedFetch = useRef(debounce((params) => fetchData(params), 500)).current;

  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    setPage(1);
    debouncedFetch({ page: 1, per_page: perPage, sort, ...(val ? { [SEARCH_FIELD]: val } : {}), ...activeFilterParamsRef.current });
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort, ...activeFilterParamsRef.current });
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    const newSort = dir ? { attribute: colId, direction: dir } : { attribute: 'id', direction: 'desc' };
    setSort(newSort);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort: newSort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...activeFilterParamsRef.current });
  }, [fetchData, perPage]);

  const handleApplyFilter = (params, tags) => {
    activeFilterParamsRef.current = params;
    setFilterTags(tags);
    setPage(1);
    setShowFilter(false);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...params });
  };

  const handleResetFilter = () => {
    activeFilterParamsRef.current = {};
    setFilterValues(getEmptyValues(FILTER_FIELDS));
    setFilterTags([]);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}) });
  };

  const removeTag = (tag) => {
    const newParams = Object.fromEntries(Object.entries(activeFilterParamsRef.current).filter(([k]) => k !== tag.id));
    activeFilterParamsRef.current = newParams;
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...newParams });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <FaShieldAlt className="tw-text-blue-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Warranty</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Manage project warranties and addendum records.</p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button type="button" onClick={() => fetchData(buildParams())} disabled={loading} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50">
              <FaSyncAlt size={12} className={loading ? 'tw-animate-spin' : ''} />
              Reload
            </button>
            <button type="button" onClick={() => dispatch(exportWarranty(buildParams()))} disabled={exporting || loading} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50">
              <FaFileExcel size={13} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-48 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Project name or J number..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              filterTags.length > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={15} />
            More Filters
            {filterTags.length > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {filterTags.length}
              </span>
            )}
          </button>

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}</span>
                <button type="button" onClick={() => removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <button type="button" onClick={handleResetFilter} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-border tw-border-red-200 hover:tw-border-red-500 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-font-medium">
              <FaTimes size={8} />
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <AgGridTable
          rowData={rowData || []}
          columnDefs={COLUMN_DEFS}
          loading={loading}
          pagination={false}
          filter={false}
          height="max(320px, calc(100dvh - 290px))"
          onSortChanged={handleSort}
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRows || 0}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
        disabled={loading}
      />

      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={FILTER_FIELDS}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="Filter Warranties"
      />
    </div>
  );
};

export default WarrantyPage;
